import React from "react";
import cn from "classnames";
import styles from "./color-swatch-picker.module.scss";

const cssClassName = "color-swatch-picker";

export interface ColorSwatchOption {
  /** Hex color (e.g. "#027680"). */
  value: string;
  /** Localized label / tooltip title. */
  label: string;
}

export interface ColorSwatchPickerProps {
  label: string;
  /** Currently chosen color. `null` = nothing selected, which only a widget
   *  saved before colours existed can produce — every caller assigns a colour,
   *  so a click always yields one. */
  value: string | null;
  swatches: ColorSwatchOption[];
  onChange: (color: string) => void;
}

export const ColorSwatchPicker = ({
  label,
  value,
  swatches,
  onChange,
}: ColorSwatchPickerProps) => (
  <div className={styles[cssClassName]}>
    {label ? (
      <label className={styles[`${cssClassName}__label`]}>{label}</label>
    ) : null}
    <div className={styles[`${cssClassName}__swatches`]}>
      {swatches.map((s) => (
        <button
          key={s.value}
          type="button"
          className={cn(styles[`${cssClassName}__swatch`], {
            [styles[`${cssClassName}__swatch--active`]]: value === s.value,
          })}
          style={{ backgroundColor: s.value }}
          onClick={() => onChange(s.value)}
          aria-label={s.label}
          title={s.label}
        />
      ))}
    </div>
  </div>
);

export const DEFAULT_TREND_LINE_COLOR = "#027680"; // scss $green — app default
export const DEFAULT_LABEL_COLOR = "#e6f1f2"; // scss $white
export const TREND_LINE_PALETTE: ColorSwatchOption[] = [
  { value: "#027680", label: "Green" }, // scss $green
  { value: "#3aacc8", label: "Blue" }, // scss $blue
  { value: "#19af00", label: "Lime" }, // scss $doneGreen
  { value: "#e1c743", label: "Yellow" }, // scss $agentYellow
  { value: "#efd899", label: "Gold" }, // scss $mutedGold
  { value: "#697477", label: "Slate" }, // scss $inactiveGrey
  { value: "#99b2bf", label: "Steel" }, // scss $steelBlueLight
  { value: "#e6f1f2", label: "White" }, // scss $white
  { value: "#c1c1ce", label: "Grey" }, // scss $grey
  { value: "#0bb4ff", label: "Sky" }, // preconfiguredColors
  { value: "#00bfa0", label: "Teal" }, // preconfiguredColors
  { value: "#50e991", label: "Mint" }, // preconfiguredColors
  { value: "#d0f400", label: "Chartreuse" }, // preconfiguredColors
  { value: "#9b19f5", label: "Purple" }, // preconfiguredColors
  { value: "#dc0ab4", label: "Magenta" }, // preconfiguredColors
  { value: "#fd7f38", label: "Orange" }, // scss $laserOrange (warning)
  { value: "#fd3838", label: "Red" }, // scss $alarmRed (critical)
  { value: "#4dd0e1", label: "Aqua" },
  { value: "#ff5252", label: "Crimson" },
  { value: "#9575cd", label: "Indigo" },
  { value: "#66bb6a", label: "Emerald" },
  { value: "#ec407a", label: "Rose" },
  { value: "#ffd54f", label: "Sunflower" },
  { value: "rgba(58, 172, 200, 0.4)", label: "Blue 40%" },
];

export const pickPaletteHex = (used: string[] = []): string => {
  const pickable = TREND_LINE_PALETTE.filter(
    (s) => !s.value.startsWith("rgba"),
  );
  const free = pickable.filter((s) => !used.includes(s.value));
  const pool = free.length > 0 ? free : pickable;
  return pool[Math.floor(Math.random() * pool.length)].value;
};
